/* FiboSheets #6: quién puede qué en una planilla compartida, el link directo
 * y las cuentas chicas del «en vivo».
 *
 * Funciones puras, sin red ni Supabase, a propósito: MiEspacio las usa al
 * dibujar, y los tests de MiEspacio mockean `services/espacio` entero. Si
 * esto viviera ahí, en esos tests serían `undefined` y la pantalla
 * reventaría por algo que no tiene nada que ver con lo que prueban.
 *
 * La regla de verdad la pone la base (RLS + el trigger
 * `espacio_planillas_reglas_de_acceso`, ver
 * 20261005100000_espacio_compartir_editar.sql): esto sólo decide qué
 * botones mostrar.
 */

export const DUENO = "dueno";
export const EDITOR = "editor";
export const LECTOR = "lector";

const bajo = (s) => String(s || "").trim().toLowerCase();

/* El rol de `yo` en una planilla ({ dueno, compartida_con, editores }):
   DUENO, EDITOR, LECTOR o null si no tengo nada que ver. Un editor que no
   está en `compartida_con` no cuenta (la base poda igual). */
export function rolEnPlanilla(p, yo) {
  const m = bajo(yo);
  if (!p || !m) return null;
  if (bajo(p.dueno) === m) return DUENO;
  const con = (p.compartida_con || p.compartidaCon || []).map(bajo);
  const eds = (p.editores || []).map(bajo);
  if (eds.includes(m) && (con.length === 0 || con.includes(m))) return EDITOR;
  if (con.includes(m)) return LECTOR;
  // La RLS me la dejó ver y no figuro: por las dudas, sólo lectura.
  return LECTOR;
}

/* ── El link directo ─────────────────────────────────────────────────────── */

/* `?planilla=<id>` sobre /mi-espacio, y no /mi-espacio/<id>: así no hace
   falta una ruta nueva en App.jsx (que comparte el prefijo con documentos y
   pendientes) y la pantalla lo lee igual con o sin router. */
export const PARAM_LINK = "planilla";

const base = () => `${process.env.PUBLIC_URL || ""}/mi-espacio`;

export function linkDePlanilla(id, origen = typeof window !== "undefined" ? window.location.origin : "") {
  return `${origen}${base()}?${PARAM_LINK}=${encodeURIComponent(id)}`;
}

/* El id que trae la URL, o null. */
export function idDelLink(busqueda = typeof window !== "undefined" ? window.location.search : "") {
  try {
    const id = new URLSearchParams(busqueda || "").get(PARAM_LINK);
    return id && id.trim() ? id.trim() : null;
  } catch {
    return null;
  }
}

/* Deja en la barra de direcciones el link de la planilla abierta (o lo saca
   al volver a la lista), sin navegar: copiar la URL de arriba o recargar la
   pestaña abre la misma planilla. `replaceState` con el MISMO `history.state`
   para no desarmarle el historial al router (que guarda ahí su clave). */
export function marcarEnLaUrl(id) {
  if (typeof window === "undefined" || !window.history || !window.history.replaceState) return;
  try {
    const url = new URL(window.location.href);
    const ahora = url.searchParams.get(PARAM_LINK);
    if ((ahora || null) === (id || null)) return;
    if (id) url.searchParams.set(PARAM_LINK, id);
    else url.searchParams.delete(PARAM_LINK);
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  } catch {
    // Una URL rara no puede romper la planilla.
  }
}

/* «No la encontré»: la API devuelve cero filas tanto si no existe como si
   está en la papelera o no me la compartieron (la RLS no distingue, y está
   bien que no lo diga). Con `.single()` eso llega como PGRST116. */
export function esSinAcceso(error) {
  if (!error) return false;
  return error.code === "PGRST116" || /0 rows|no rows/i.test(error.details || error.message || "");
}

/* ── En vivo ─────────────────────────────────────────────────────────────── */

/* El mismo instante aunque venga escrito distinto: la API devuelve
   «2026-10-05T10:05:00.123+00:00» y el realtime puede mandar
   «2026-10-05 10:05:00.123+00». Lo que no se entiende como fecha se compara
   como texto, tal cual. */
export function instante(s) {
  if (!s) return NaN;
  let t = String(s).trim().replace(" ", "T");
  if (/[+-]\d\d$/.test(t)) t += ":00";
  return Date.parse(t);
}
const clave = (s) => {
  const t = instante(s);
  return Number.isNaN(t) ? String(s) : t;
};
export function mismoInstante(a, b) {
  if (!a || !b) return false;
  return clave(a) === clave(b);
}

/* Los guardados de ESTA pestaña, para reconocer el eco: el realtime avisa
   también de lo que guardé yo, y a veces llega después de que ya salió el
   guardado siguiente (cuando `base` ya es otro). Sin esto, escribir de
   corrido se leería como «otro cambió la planilla». Las últimas 50 alcanzan. */
export function anotarPropio(G, actualizadoEn) {
  if (!G || !actualizadoEn) return;
  if (!G.propios) G.propios = [];
  G.propios.push(clave(actualizadoEn));
  if (G.propios.length > 50) G.propios.shift();
}
export function esPropio(G, actualizadoEn) {
  if (!G || !actualizadoEn) return false;
  if (mismoInstante(actualizadoEn, G.base)) return true;
  return (G.propios || []).includes(clave(actualizadoEn));
}

/* Quién guardó, para el cartel: el mail, «una automatización» para el bot, o
   «vos (en otra pestaña)». */
export function quienGuardo(por, yo) {
  const p = bajo(por);
  if (!p) return "Alguien";
  if (p === "bot") return "Una automatización";
  if (p === bajo(yo)) return "Vos (en otra pestaña)";
  return p;
}
